import { Vector3 } from '@babylonjs/core/Maths/math.vector.js';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder.js';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial.js';
import { Color3 } from '@babylonjs/core/Maths/math.color.js';

type TargetData = {
  mesh: any;
  position: Vector3;
  active: boolean;
  timeLeft: number;
};

class TargetRangeController {
  game: any;
  stationPosition: Vector3;
  isActive: boolean;
  score: number;
  timeRemaining: number;
  targets: TargetData[];
  scoreEl: HTMLElement | null;
  timerEl: HTMLElement | null;
  overlayEl: HTMLElement | null;
  _spawnTimer: number;
  _maxTime: number;
  _targetLifetime: number;

  constructor(game: any, stationPosition: Vector3) {
    this.game = game;
    this.stationPosition = stationPosition;
    this.isActive = false;
    this.score = 0;
    this.timeRemaining = 0;
    this.targets = [];
    this.scoreEl = null;
    this.timerEl = null;
    this.overlayEl = null;
    this._spawnTimer = 0;
    this._maxTime = 30;
    this._targetLifetime = 4;
  }

  startGame() {
    if (this.isActive) return;
    this.isActive = true;
    this.score = 0;
    this.timeRemaining = this._maxTime;
    this.targets = [];
    this._spawnTimer = 0;

    this.createUI();
    this.spawnTarget();
  }

  createUI() {
    // Remove existing overlay if any
    this.disposeUI();

    const overlay = document.createElement('div');
    overlay.id = 'target-range-overlay';
    overlay.className = 'target-range-overlay';
    overlay.innerHTML =
      '<div class="target-range-header">TARGET RANGE</div>' +
      '<div class="target-range-stats">' +
      '<span>Score: <span id="target-score">0</span></span>' +
      '<span>Time: <span id="target-timer">30</span>s</span>' +
      '</div>';
    document.body.appendChild(overlay);
    this.overlayEl = overlay;
    this.scoreEl = document.getElementById('target-score');
    this.timerEl = document.getElementById('target-timer');
  }

  spawnTarget() {
    if (!this.isActive) return;
    const game = this.game;
    if (!game.scene) return;

    // Random position within the platform area
    const xOff = (Math.random() - 0.5) * 6;
    const zOff = (Math.random() - 0.5) * 4;
    const targetPos = new Vector3(
      this.stationPosition.x + xOff,
      1.0,
      this.stationPosition.z + zOff
    );

    // Create target disc (red bullseye)
    const target = MeshBuilder.CreateCylinder('targetRangeTarget_' + Date.now(), {
      diameterTop: 0.4,
      diameterBottom: 0.4,
      height: 0.05,
      tessellation: 12
    }, game.scene);
    target.position = targetPos;

    const targetMat = new StandardMaterial('targetMat_' + Date.now(), game.scene);
    targetMat.diffuseColor = new Color3(1, 0.2, 0.2);
    targetMat.emissiveColor = new Color3(0.5, 0.1, 0.1);
    targetMat.specularColor = Color3.Black();
    target.material = targetMat;

    // Inner bullseye
    const bullseye = MeshBuilder.CreateCylinder('targetBullseye_' + Date.now(), {
      diameterTop: 0.15,
      diameterBottom: 0.15,
      height: 0.06,
      tessellation: 12
    }, game.scene);
    bullseye.position = new Vector3(targetPos.x, targetPos.y + 0.03, targetPos.z);
    const bullseyeMat = new StandardMaterial('targetBullseyeMat_' + Date.now(), game.scene);
    bullseyeMat.diffuseColor = new Color3(1, 1, 1);
    bullseyeMat.emissiveColor = new Color3(0.8, 0.8, 0.8);
    bullseye.material = bullseyeMat;

    // Make target pickable for shooting detection
    target.isPickable = true;
    target.metadata = { isTarget: true, controller: this };

    this.targets.push({
      mesh: target,
      position: targetPos,
      active: true,
      timeLeft: this._targetLifetime
    });
  }

  hitTarget(targetMesh: any) {
    const idx = this.targets.findIndex(t => t.mesh === targetMesh);
    if (idx === -1) return;

    const target = this.targets[idx];
    if (!target.active) return;
    target.active = false;

    // Score points
    this.score += 10;
    if (this.scoreEl) {
      this.scoreEl.textContent = String(this.score);
    }

    // Visual feedback - flash and disappear
    if (target.mesh) {
      target.mesh.dispose();
    }
    this.targets.splice(idx, 1);
  }

  update(delta: number) {
    if (!this.isActive) return;

    this.timeRemaining -= delta;
    if (this.timerEl) {
      this.timerEl.textContent = String(Math.ceil(this.timeRemaining));
    }

    // Spawn new targets periodically
    this._spawnTimer += delta;
    if (this._spawnTimer >= 1.5 && this.targets.length < 5) {
      this._spawnTimer = 0;
      this.spawnTarget();
    }

    // Remove expired targets
    for (let i = this.targets.length - 1; i >= 0; i--) {
      const t = this.targets[i];
      t.timeLeft -= delta;
      if (t.timeLeft <= 0) {
        if (t.mesh) t.mesh.dispose();
        this.targets.splice(i, 1);
      }
    }

    // Check for shooting at targets
    if (this.game.playerCombatController && this.game.playerCombatController._lastHitTarget) {
      const hit = this.game.playerCombatController._lastHitTarget;
      if (hit.metadata && hit.metadata.isTarget) {
        hit.metadata.controller.hitTarget(hit);
      }
    }

    // End game when time runs out
    if (this.timeRemaining <= 0) {
      this.endGame();
    }
  }

  endGame() {
    this.isActive = false;
    this.disposeUI();

    // Show result
    const resultEl = document.createElement('div');
    resultEl.className = 'target-range-result';
    resultEl.innerHTML =
      '<div class="target-range-result-title">TARGET RANGE COMPLETE</div>' +
      '<div class="target-range-result-score">Score: ' + this.score + '</div>' +
      '<button class="target-range-result-close">Close</button>';
    document.body.appendChild(resultEl);

    resultEl.querySelector('.target-range-result-close')?.addEventListener('click', () => {
      if (resultEl.parentNode) resultEl.parentNode.removeChild(resultEl);
    });

    // Auto-close after 10 seconds
    setTimeout(() => {
      if (resultEl.parentNode) resultEl.parentNode.removeChild(resultEl);
    }, 10000);

    // Clean up remaining targets
    for (const t of this.targets) {
      if (t.mesh) t.mesh.dispose();
    }
    this.targets = [];
  }

  disposeUI() {
    if (this.overlayEl && this.overlayEl.parentNode) {
      this.overlayEl.parentNode.removeChild(this.overlayEl);
    }
    this.overlayEl = null;
    this.scoreEl = null;
    this.timerEl = null;
  }

  dispose() {
    this.isActive = false;
    this.disposeUI();
    for (const t of this.targets) {
      if (t.mesh) t.mesh.dispose();
    }
    this.targets = [];
  }
}

export { TargetRangeController };
